"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";
import type { JobStatus } from "@easyshot/shared";
import { AdminAuthError, adminFetch } from "@/lib/admin-client";
import { AdminKeyGate } from "@/components/admin-key-gate";
import { AvatarPortrait } from "@/components/avatar-portrait";
import type { GraphEdge } from "@/lib/workflow-graph";
import {
  DND_MIME,
  MODULE_META,
  WorkflowCanvas,
  type CanvasNodeSpec,
  type CanvasTemplate,
} from "@/components/workflow-canvas";
import { Badge, Button, Card, Spinner, inputClass } from "@/components/ui";

interface JobLite {
  style: string;
  status: JobStatus;
}

interface AssetAvatar {
  id: string;
  name: string;
  locale: string;
  premium: boolean;
  enabled: boolean;
}

interface AssetVoice {
  id: string;
  name: string;
  locale: string;
  speed: number;
  enabled: boolean;
}

interface AssetProduct {
  id: string;
  title: string;
  source: string;
}

interface SchemaElement {
  key: string;
  label: string;
  provider?: string;
}

/** 各模块支持的输入形式 */
const INPUT_OPTIONS: Record<string, [string, string][]> = {
  topic: [
    ["topic", "Topic / subject text"],
    ["keywords", "Keywords"],
    ["brief", "Creative brief / notes"],
    ["source_url", "Reference URL"],
  ],
  product: [
    ["url", "Product URL (scrape)"],
    ["text", "Pasted description"],
    ["images", "Uploaded images"],
    ["video", "Uploaded video"],
  ],
  avatar: [
    ["preset", "Preset roster"],
    ["photo", "Customer photo (photo avatar)"],
    ["clone", "Customer footage (digital twin clone)"],
  ],
  voice: [
    ["preset", "Preset voices"],
    ["recording", "Customer recording (voice clone)"],
  ],
};

/** 抠像键色：绿幕是通用默认，蓝幕留给绿色服装/道具的场景 */
const KEY_COLORS: [string, string][] = [
  ["#00B140", "Green (#00B140) — broadcast standard"],
  ["#0047BB", "Blue (#0047BB) — for green wardrobe"],
];

/** 交付格式：绿底成片人人能用；带 alpha 的省一步抠像但体积大、兼容性差 */
const CHROMA_DELIVERABLES: [string, string][] = [
  ["composited_mp4", "Background-composited MP4 (H.264)"],
  ["greenscreen_mp4", "Green-screen MP4 (H.264)"],
  ["alpha_webm", "Transparent WebM (VP9 + alpha)"],
  ["alpha_mov", "Transparent MOV (ProRes 4444)"],
  ["matte_mp4", "Separate matte pass (luma MP4)"],
];

const PROMPT_REFERENCE_TOKENS: [string, string][] = [
  ["First image", "@Image 1"],
  ["Second image", "@Image 2"],
  ["First video", "@Video 1"],
  ["Second video", "@Video 2"],
  ["First audio", "@Audio 1"],
];

const VIDEO_ENGINES: Record<string, [string, string][]> = {
  seedance: [
    ["dreamina-seedance-2-0-260128", "Seedance 2.0"],
    ["dreamina-seedance-2-0-fast-260128", "Seedance 2.0 Fast"],
    ["dreamina-seedance-2-5-260628", "Seedance 2.5"],
  ],
  heygen: [
    ["avatar_iii", "HeyGen Avatar III"],
    ["avatar_iv", "HeyGen Avatar IV"],
  ],
  heygen_clone: [["heygen_clone", "HeyGen custom avatar clone"]],
  kickart: [["kickart", "Kickart product video"]],
  minimax_h3: [
    ["minimax/h3-max/reference-to-video", "MiniMax H3 Max · fal.ai"],
    ["MiniMax-H3", "MiniMax H3 · official fallback"],
  ],
  infinitetalk: [
    [
      "infinitetalk-auto",
      "Automatic · configured RunPod Hub, self-hosted, or fal backend",
    ],
    ["runpod/infinitetalk", "InfiniteTalk · RunPod Hub public API 480p"],
    [
      "fal-ai/infinitetalk/single-text",
      "InfiniteTalk - fal.ai talking photo + built-in voice 480p",
    ],
    ["infinitetalk-single", "InfiniteTalk - RunPod self-hosted 480p"],
  ],
  mock: [["mock", "Mock renderer (testing only)"]],
};

const VIDEO_PROVIDERS: [string, string][] = [
  ["seedance", "Seedance · BytePlus ModelArk"],
  ["heygen", "HeyGen"],
  ["heygen_clone", "HeyGen Clone"],
  ["kickart", "Kickart"],
  ["minimax_h3", "MiniMax H3"],
  ["infinitetalk", "InfiniteTalk"],
  ["mock", "Mock · testing"],
];

const LLM_MODELS: Record<string, [string, string][]> = {
  modelark: [
    ["seed-2-0-mini-260215", "Dola Seed 2.0 Mini"],
    ["deepseek-v3-2", "DeepSeek V3.2"],
  ],
  anthropic: [
    ["claude-sonnet-5", "Claude Sonnet 5"],
    ["claude-opus-5", "Claude Opus 5"],
  ],
};

const MATERIAL_PROVIDERS: [string, string][] = [
  ["product_uploads", "Customer product uploads"],
  ["product_page", "Images and video extracted from product URL"],
  ["pexels", "Pexels stock footage"],
  ["pixabay", "Pixabay stock footage"],
  ["coverr", "Coverr stock footage"],
  ["seedance", "Seedance AI fallback"],
];

const PUBLISH_PLATFORMS: [string, string][] = [
  ["tiktok", "TikTok"],
  ["instagram", "Instagram Reels"],
  ["youtube", "YouTube Shorts"],
];

const MEDIA_KINDS: [string, string][] = [
  ["image", "Images"],
  ["video", "Video"],
  ["audio", "Audio"],
];

export default function AdminWorkflowPage() {
  const [jobs, setJobs] = useState<JobLite[] | null>(null);
  const [templates, setTemplates] = useState<CanvasTemplate[]>([]);
  const [avatars, setAvatars] = useState<AssetAvatar[]>([]);
  const [voices, setVoices] = useState<AssetVoice[]>([]);
  const [products, setProducts] = useState<AssetProduct[]>([]);
  const [schemas, setSchemas] = useState<Record<string, SchemaElement[]>>({});
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<string>("");
  const [settingsKey, setSettingsKey] = useState<string | null>(null);
  const [needKey, setNeedKey] = useState(false);
  // Canvas writes must reach the database in the same order as the gestures.
  // Otherwise an older, slower PATCH can arrive last and pull a node backwards.
  const graphSaveQueue = useRef<Promise<void>>(Promise.resolve());

  const loadJobs = useCallback(async () => {
    try {
      const r = await adminFetch<{ jobs: JobLite[] }>("/api/admin/jobs");
      setJobs(r.jobs);
      setNeedKey(false);
    } catch (e) {
      if (e instanceof AdminAuthError) setNeedKey(true);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [tpls, avs, vos, prods, sp, sa, sv] = await Promise.all([
        adminFetch<CanvasTemplate[]>("/api/admin/templates"),
        adminFetch<AssetAvatar[]>("/api/admin/assets/avatars"),
        adminFetch<AssetVoice[]>("/api/admin/assets/voices"),
        adminFetch<AssetProduct[]>("/api/admin/assets/products"),
        adminFetch<{ elements: SchemaElement[] }>("/api/admin/schemas/product"),
        adminFetch<{ elements: SchemaElement[] }>("/api/admin/schemas/avatar"),
        adminFetch<{ elements: SchemaElement[] }>("/api/admin/schemas/voice"),
      ]);
      setTemplates(tpls);
      setSelected((s) =>
        tpls.some((t) => t.id === s) ? s : (tpls[0]?.id ?? ""),
      );
      setAvatars(avs);
      setVoices(vos);
      setProducts(prods);
      setSchemas({
        product: sp.elements,
        avatar: sa.elements,
        voice: sv.elements,
      });
      setLoaded(true);
    } catch {
      // 401 由 loadJobs 统一处理
    }
  }, []);

  useEffect(() => {
    void loadJobs();
    void loadData();
    const t = setInterval(loadJobs, 3000);
    return () => clearInterval(t);
  }, [loadJobs, loadData]);

  const selectedTemplate = templates.find((t) => t.id === selected);

  const patchTemplate = useCallback(
    async (patch: Record<string, unknown>) => {
      if (!selectedTemplate) return;
      const updated = await adminFetch<CanvasTemplate>(
        `/api/admin/templates/${selectedTemplate.id}`,
        { method: "PATCH", body: JSON.stringify(patch) },
      );
      setTemplates((cur) =>
        cur.map((t) => (t.id === updated.id ? updated : t)),
      );
    },
    [selectedTemplate],
  );

  const saveGraph = useCallback(
    (nodes: CanvasNodeSpec[], edges: GraphEdge[]) => {
      if (!selectedTemplate) return;
      const templateId = selectedTemplate.id;
      const canvas = { nodes, edges };

      // Pin the node immediately where the administrator dropped it. Network
      // latency must never control the visible canvas position.
      setTemplates((current) =>
        current.map((template) =>
          template.id === templateId ? { ...template, canvas } : template,
        ),
      );

      graphSaveQueue.current = graphSaveQueue.current
        .then(async () => {
          const updated = await adminFetch<CanvasTemplate>(
            `/api/admin/templates/${templateId}`,
            { method: "PATCH", body: JSON.stringify({ canvas }) },
          );
          setTemplates((current) =>
            current.map((template) =>
              template.id === updated.id
                // Preserve the newest optimistic canvas while copying every
                // other server-confirmed field from this response.
                ? { ...updated, canvas: template.canvas }
                : template,
            ),
          );
        })
        .catch(() => {
          // Keep the locally pinned position. A later gesture retries the
          // ordered queue; the admin should never see a network-induced jump.
        });
    },
    [selectedTemplate],
  );

  if (needKey) {
    return (
      <AdminKeyGate
        onUnlock={() => {
          void loadJobs();
          void loadData();
        }}
      />
    );
  }

  if (!jobs || !loaded || !selectedTemplate) {
    return (
      <div className="grid min-h-[60vh] place-items-center text-muted">
        <Spinner className="size-6" />
      </div>
    );
  }

  const templateJobs = jobs.filter((j) => j.style === selected);
  const canvasStats = {
    products: products.length,
    avatarsLive: avatars.filter((a) => a.enabled).length,
    avatarsTotal: avatars.length,
    voicesLive: voices.filter((v) => v.enabled).length,
    voicesTotal: voices.length,
    queued: templateJobs.filter((j) => j.status === "queued").length,
    active: templateJobs.filter(
      (j) => j.status === "scripting" || j.status === "rendering",
    ).length,
    succeeded: templateJobs.filter((j) => j.status === "succeeded").length,
    failed: templateJobs.filter((j) => j.status === "failed").length,
  };

  const onCanvas = new Set(
    (selectedTemplate.canvas?.nodes ?? Object.keys(MODULE_META).map((key) => ({ key })) ).map(
      (n) => n.key,
    ),
  );

  return (
    <div className="mx-auto max-w-6xl px-5 py-8 sm:py-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Workflow</h1>
          <p className="mt-2 text-sm text-muted">
            Drag modules from the library onto the canvas, drag nodes to
            arrange, ⚙ to configure each module. Layout saves per template.
          </p>
        </div>
        <select
          className={`${inputClass} w-auto min-w-48`}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          {templates.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
              {t.enabled ? "" : " (disabled)"}
            </option>
          ))}
        </select>
      </div>

      <WorkflowCanvas
        template={selectedTemplate}
        stats={canvasStats}
        onGraphChange={saveGraph}
        onConfigure={setSettingsKey}
      />

      {/* ── 模块库：拖到画布 / 点击设置 ─────────────────── */}
      <h2 className="mt-8 mb-3 text-sm font-medium text-muted">
        Module library — drag onto the canvas, or ⚙ configure
      </h2>
      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {Object.entries(MODULE_META).map(([key, meta]) => (
          <Card
            key={key}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_MIME, key);
              e.dataTransfer.effectAllowed = "move";
            }}
            className="cursor-grab p-4 transition select-none hover:border-[var(--border-strong)] active:cursor-grabbing"
          >
            <div className="flex items-start justify-between">
              <div className="grid size-9 place-items-center rounded-xl bg-white/8 text-base">
                {meta.icon}
              </div>
              {!onCanvas.has(key) && (
                <button
                  type="button"
                  className="rounded-lg bg-white/8 px-2 py-1 text-[10px] text-white/60 hover:bg-white/15"
                  // 只加节点、不动已有连线——接线交给管理员在画布上拖，
                  // 自动补线会把自定义的接法冲掉
                  onClick={() =>
                    saveGraph(
                      [
                        ...(selectedTemplate.canvas?.nodes ?? []),
                        { key, x: 320, y: 480 },
                      ],
                      selectedTemplate.canvas?.edges ?? [],
                    )
                  }
                >
                  + Add
                </button>
              )}
            </div>
            <div className="mt-2.5 text-[12px] font-semibold">{meta.title}</div>
            <div className="mt-2 flex items-center gap-2">
              {meta.configurable && (
                <button
                  type="button"
                  className="rounded-lg bg-white/8 px-2 py-1 text-[11px] text-white/60 transition hover:bg-white/15 hover:text-white"
                  onClick={() => setSettingsKey(key)}
                >
                  ⚙ Configure
                </button>
              )}
            </div>
          </Card>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-white/30">
        Deep-dive pages:{" "}
        <Link href="/admin/products" className="hover:text-foreground">
          Product Twins
        </Link>{" "}
        ·{" "}
        <Link href="/admin/avatars" className="hover:text-foreground">
          Avatars
        </Link>{" "}
        ·{" "}
        <Link href="/admin/voices" className="hover:text-foreground">
          Voices
        </Link>{" "}
        ·{" "}
        <Link href="/admin/templates" className="hover:text-foreground">
          Templates
        </Link>{" "}
        ·{" "}
        <Link href="/admin/jobs" className="hover:text-foreground">
          Job log
        </Link>
      </p>

      {settingsKey && (
        <ModuleSettings
          moduleKey={settingsKey}
          template={selectedTemplate}
          avatars={avatars}
          voices={voices}
          schemas={schemas}
          onClose={() => setSettingsKey(null)}
          onSave={async (patch) => {
            await patchTemplate(patch);
            setSettingsKey(null);
          }}
        />
      )}
    </div>
  );
}

/** ── 模块设置抽屉 ──────────────────────────────────────── */
function ModuleSettings({
  moduleKey,
  template,
  avatars,
  voices,
  schemas,
  onClose,
  onSave,
}: {
  moduleKey: string;
  template: CanvasTemplate;
  avatars: AssetAvatar[];
  voices: AssetVoice[];
  schemas: Record<string, SchemaElement[]>;
  onClose: () => void;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const meta = MODULE_META[moduleKey];
  const cfg = template.moduleConfig?.[moduleKey] ?? {};

  const [tone, setTone] = useState(template.tone);
  const [providerType, setProviderType] = useState(template.provider.type);
  const [engine, setEngine] = useState(template.provider.engine ?? "");
  const [endpoint, setEndpoint] = useState(template.provider.endpoint ?? "");
  const [pool, setPool] = useState<string[]>(
    moduleKey === "avatar" ? template.avatarIds : template.voiceIds,
  );
  const [inputs, setInputs] = useState<string[]>(
    cfg.inputs ?? (INPUT_OPTIONS[moduleKey]?.slice(0, 2).map(([k]) => k) ?? []),
  );
  const [outputs, setOutputs] = useState<string[]>(
    cfg.outputs ?? (schemas[moduleKey]?.slice(0, 4).map((e) => e.key) ?? []),
  );
  const [keyColor, setKeyColor] = useState(
    cfg.keyColor ?? KEY_COLORS[0][0],
  );
  const [deliverables, setDeliverables] = useState<string[]>(
    cfg.deliverables ??
      (template.provider.type === "infinitetalk"
        ? ["composited_mp4"]
        : ["greenscreen_mp4"]),
  );
  const [promptText, setPromptText] = useState(cfg.promptText ?? "");
  const [negativePrompt, setNegativePrompt] = useState(cfg.negativePrompt ?? "");
  const [referencePolicy, setReferencePolicy] = useState(
    cfg.referencePolicy ??
      "Preserve the exact product geometry, proportions, materials, colors, packaging, logo, labels, and component positions from the supplied reference images.",
  );
  const aigcCfg = template.moduleConfig?.aigc;
  const [videoProvider, setVideoProvider] = useState(
    aigcCfg?.videoProvider ?? template.provider.type,
  );
  const [videoEngine, setVideoEngine] = useState(
    aigcCfg?.videoEngine ?? template.provider.engine ?? "",
  );
  const [generationMode, setGenerationMode] = useState(
    aigcCfg?.generationMode ?? "reference-to-video",
  );
  const [imageProvider, setImageProvider] = useState(
    cfg.imageProvider ?? "runninghub",
  );
  const [imageEngine, setImageEngine] = useState(
    cfg.imageEngine ?? "gpt-image-1.5/image-to-image/stable",
  );
  const [imageAspectRatio, setImageAspectRatio] = useState(
    cfg.imageAspectRatio ?? "9:16",
  );
  const llmCfg = template.moduleConfig?.llm;
  const [llmProvider, setLlmProvider] = useState(
    llmCfg?.llmProvider ?? "modelark",
  );
  const [llmModel, setLlmModel] = useState(
    llmCfg?.llmModel ?? "seed-2-0-mini-260215",
  );
  const [llmPurpose, setLlmPurpose] = useState(
    llmCfg?.llmPurpose ?? "product-analysis-and-script",
  );
  const [keywordCount, setKeywordCount] = useState(cfg.keywordCount ?? 3);
  const [keywordLanguage, setKeywordLanguage] = useState(cfg.keywordLanguage ?? "en");
  const [matchScriptOrder, setMatchScriptOrder] = useState(cfg.matchScriptOrder ?? true);
  const [materialProviders, setMaterialProviders] = useState<string[]>(
    cfg.materialProviders ?? ["product_uploads", "product_page", "pexels", "pixabay", "coverr", "seedance"],
  );
  const [materialMode, setMaterialMode] = useState(cfg.materialMode ?? "hybrid");
  const [materialFallback, setMaterialFallback] = useState(
    cfg.materialFallback ?? "ai_video",
  );
  const [clipDurationSec, setClipDurationSec] = useState(cfg.clipDurationSec ?? 3);
  const [stockLicenseRequired, setStockLicenseRequired] = useState(
    cfg.stockLicenseRequired ?? true,
  );
  const [ttsProvider, setTtsProvider] = useState(cfg.ttsProvider === "custom_audio" ? "custom_audio" : "fish_audio");
  const [ttsVoiceId, setTtsVoiceId] = useState(cfg.ttsVoiceId ?? "");
  const [ttsSpeed, setTtsSpeed] = useState(cfg.ttsSpeed ?? 1);
  const [ttsVolume, setTtsVolume] = useState(cfg.ttsVolume ?? 1);
  const [subtitleEnabled, setSubtitleEnabled] = useState(cfg.subtitleEnabled ?? true);
  const [subtitleMode, setSubtitleMode] = useState(cfg.subtitleMode ?? "sentence");
  const [subtitleFont, setSubtitleFont] = useState(cfg.subtitleFont ?? "Inter");
  const [subtitleSize, setSubtitleSize] = useState(cfg.subtitleSize ?? 64);
  const [subtitleColor, setSubtitleColor] = useState(cfg.subtitleColor ?? "#FFFFFF");
  const [subtitleHighlightColor, setSubtitleHighlightColor] = useState(
    cfg.subtitleHighlightColor ?? "#FFD84D",
  );
  const [subtitlePosition, setSubtitlePosition] = useState(cfg.subtitlePosition ?? "bottom");
  const [bgmEnabled, setBgmEnabled] = useState(cfg.bgmEnabled ?? true);
  const [bgmSource, setBgmSource] = useState(cfg.bgmSource ?? "library");
  const [bgmTrackId, setBgmTrackId] = useState(cfg.bgmTrackId ?? "");
  const [bgmVolumeDb, setBgmVolumeDb] = useState(cfg.bgmVolumeDb ?? -22);
  const [voiceDuckingDb, setVoiceDuckingDb] = useState(cfg.voiceDuckingDb ?? -6);
  const [width, setWidth] = useState(cfg.width ?? 1080);
  const [height, setHeight] = useState(cfg.height ?? 1920);
  const [fps, setFps] = useState(cfg.fps ?? 30);
  const [transition, setTransition] = useState(cfg.transition ?? "fade");
  const [transitionDurationMs, setTransitionDurationMs] = useState(
    cfg.transitionDurationMs ?? 350,
  );
  const [publishEnabled, setPublishEnabled] = useState(cfg.publishEnabled ?? false);
  const [publishPlatforms, setPublishPlatforms] = useState<string[]>(
    cfg.publishPlatforms ?? [],
  );
  const [mediaLabel, setMediaLabel] = useState(cfg.mediaLabel ?? "Media");
  const [mediaSource, setMediaSource] = useState(
    cfg.mediaSource ?? "customer_upload",
  );
  const [mediaKinds, setMediaKinds] = useState<string[]>(
    cfg.mediaKinds ?? ["image", "video"],
  );
  const [mediaMaxItems, setMediaMaxItems] = useState(cfg.mediaMaxItems ?? 4);
  const [mediaRequired, setMediaRequired] = useState(cfg.mediaRequired ?? true);
  const [valueName, setValueName] = useState(cfg.valueName ?? "value");
  const [valueType, setValueType] = useState(cfg.valueType ?? "text");
  const [valueDefault, setValueDefault] = useState(cfg.valueDefault ?? "");
  const [valueDescription, setValueDescription] = useState(
    cfg.valueDescription ?? "",
  );
  const [valueRequired, setValueRequired] = useState(cfg.valueRequired ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const patch: Record<string, unknown> = {};
      if (moduleKey === "script") patch.tone = tone;
      if (moduleKey === "render") {
        patch.provider = {
          type: providerType,
          engine: engine || undefined,
          endpoint: endpoint || undefined,
        };
      }
      if (moduleKey === "avatar") patch.avatarIds = pool;
      if (moduleKey === "voice") patch.voiceIds = pool;
      if (moduleKey === "chroma") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          chroma: { keyColor, deliverables },
        };
      }
      if (moduleKey === "prompt") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          prompt: { promptText, negativePrompt, referencePolicy },
        };
      }
      if (moduleKey === "image_compose") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          image_compose: {
            inputs: cfg.inputs ?? ["person_reference", "background_reference"],
            outputs: cfg.outputs ?? ["composite_image"],
            imageProvider,
            imageEngine,
            imageAspectRatio,
            promptText,
          },
        };
      }
      if (moduleKey === "aigc") {
        patch.provider = {
          type: videoProvider,
          engine: videoEngine || undefined,
        };
        patch.moduleConfig = {
          ...template.moduleConfig,
          aigc: { videoProvider, videoEngine, generationMode },
        };
      }
      if (moduleKey === "infinitetalk") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          infinitetalk: {
            inputs: cfg.inputs ?? [
              "image.url",
              "audio.url",
              "prompt",
            ],
            outputs: cfg.outputs ?? ["job_id", "video.mp4", "duration_sec"],
            videoProvider: "infinitetalk",
            videoEngine: "runpod/infinitetalk",
          },
        };
      }
      if (moduleKey === "llm") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          llm: { llmProvider, llmModel, llmPurpose },
        };
      }
      if (moduleKey === "keywords") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          keywords: { keywordCount, keywordLanguage, matchScriptOrder },
        };
      }
      if (moduleKey === "materials") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          materials: {
            materialMode,
            materialProviders,
            materialFallback,
            clipDurationSec,
            stockLicenseRequired,
          },
        };
      }
      if (moduleKey === "tts") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          tts: { ttsProvider, ttsVoiceId, ttsSpeed, ttsVolume },
        };
      }
      if (moduleKey === "subtitles") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          subtitles: {
            subtitleEnabled,
            subtitleMode,
            subtitleFont,
            subtitleSize,
            subtitleColor,
            subtitleHighlightColor,
            subtitlePosition,
          },
        };
      }
      if (moduleKey === "bgm") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          bgm: { bgmEnabled, bgmSource, bgmTrackId, bgmVolumeDb, voiceDuckingDb },
        };
      }
      if (moduleKey === "compose") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          compose: { width, height, fps, transition, transitionDurationMs },
        };
      }
      if (moduleKey === "publish") {
        patch.moduleConfig = {
          ...template.moduleConfig,
          publish: { publishEnabled, publishPlatforms },
        };
      }
      if (moduleKey === "media") {
        if (!mediaLabel.trim()) throw new Error("Media label is required");
        if (mediaKinds.length === 0) {
          throw new Error("Select at least one media type");
        }
        patch.moduleConfig = {
          ...template.moduleConfig,
          media: {
            inputs: [],
            outputs: ["media"],
            mediaLabel: mediaLabel.trim(),
            mediaSource,
            mediaKinds,
            mediaMaxItems: Math.max(1, Math.min(20, mediaMaxItems)),
            mediaRequired,
          },
        };
      }
      if (moduleKey === "value") {
        if (!valueName.trim()) throw new Error("Parameter name is required");
        if (valueType === "json" && valueDefault.trim()) {
          try {
            JSON.parse(valueDefault);
          } catch {
            throw new Error("Default JSON is not valid");
          }
        }
        if (
          valueType === "number" &&
          valueDefault.trim() &&
          !Number.isFinite(Number(valueDefault))
        ) {
          throw new Error("Default value must be a number");
        }
        patch.moduleConfig = {
          ...template.moduleConfig,
          value: {
            inputs: [],
            outputs: ["value"],
            valueName: valueName.trim(),
            valueType,
            valueDefault,
            valueDescription: valueDescription.trim(),
            valueRequired,
          },
        };
      }
      if (INPUT_OPTIONS[moduleKey]) {
        patch.moduleConfig = {
          ...template.moduleConfig,
          [moduleKey]: { inputs, outputs },
        };
      }
      await onSave(patch);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      setBusy(false);
    }
  }

  const checkboxCls =
    "flex items-center gap-2 rounded-lg border border-line px-3 py-2 text-[12px] cursor-pointer transition hover:border-[var(--border-strong)]";

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto border-l border-line bg-[#0e0e17] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="grid size-9 place-items-center rounded-xl bg-white/8">
              {meta.icon}
            </div>
            <div>
              <div className="text-sm font-semibold">{meta.title}</div>
              <div className="text-[11px] text-muted">
                Template: {template.name}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid size-8 place-items-center rounded-lg bg-white/8 text-white/60 hover:bg-white/15"
          >
            ✕
          </button>
        </div>

        {err && (
          <div className="mt-4 rounded-lg border border-[var(--danger)]/40 bg-[var(--danger)]/10 px-3 py-2 text-[12px] text-[var(--danger)]">
            {err}
          </div>
        )}

        {moduleKey === "media" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Input name</div>
              <input className={inputClass} value={mediaLabel} onChange={(e) => setMediaLabel(e.target.value)} placeholder="Example: Product reference media" />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Media source</div>
              <select className={inputClass} value={mediaSource} onChange={(e) => setMediaSource(e.target.value)}>
                <option value="customer_upload">Customer upload</option>
                <option value="product_assets">Product Digital Twin assets</option>
                <option value="external_url">External media URL</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Accepted media types</div>
              <div className="grid gap-2">
                {MEDIA_KINDS.map(([kind, label]) => (
                  <label key={kind} className={checkboxCls}>
                    <input type="checkbox" checked={mediaKinds.includes(kind)} onChange={() => toggle(mediaKinds, setMediaKinds, kind)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Maximum files</div>
              <input className={inputClass} type="number" min={1} max={20} value={mediaMaxItems} onChange={(e) => setMediaMaxItems(Number(e.target.value))} />
            </div>
            <label className={checkboxCls}>
              <input type="checkbox" checked={mediaRequired} onChange={(e) => setMediaRequired(e.target.checked)} />
              Require this media before the workflow can run
            </label>
          </section>
        )}

        {moduleKey === "value" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Parameter name</div>
              <input className={inputClass} value={valueName} onChange={(e) => setValueName(e.target.value)} placeholder="Example: duration" />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Value type</div>
              <select className={inputClass} value={valueType} onChange={(e) => { setValueType(e.target.value); setValueDefault(""); }}>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="boolean">True / false</option>
                <option value="json">JSON</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Default value</div>
              {valueType === "boolean" ? (
                <select className={inputClass} value={valueDefault} onChange={(e) => setValueDefault(e.target.value)}>
                  <option value="">No default</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </select>
              ) : valueType === "json" ? (
                <textarea className={`${inputClass} min-h-28 font-mono text-[12px]`} value={valueDefault} onChange={(e) => setValueDefault(e.target.value)} placeholder='{"key": "value"}' />
              ) : (
                <input className={inputClass} type={valueType === "number" ? "number" : "text"} value={valueDefault} onChange={(e) => setValueDefault(e.target.value)} placeholder="Optional" />
              )}
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Description</div>
              <textarea className={`${inputClass} min-h-24`} value={valueDescription} onChange={(e) => setValueDescription(e.target.value)} placeholder="Explain how downstream modules should use this value" />
            </div>
            <label className={checkboxCls}>
              <input type="checkbox" checked={valueRequired} onChange={(e) => setValueRequired(e.target.checked)} />
              Require a value before the workflow can run
            </label>
          </section>
        )}

        {/* 脚本引擎：语气 */}
        {moduleKey === "script" && (
          <section className="mt-6">
            <div className="mb-2 text-[12px] font-medium text-muted">
              Tone prompt (fed to the script LLM)
            </div>
            <textarea
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              className={`${inputClass} min-h-32`}
            />
          </section>
        )}

        {/* 渲染 provider */}
        {moduleKey === "prompt" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Reference asset tokens
              </div>
              <div className="overflow-hidden rounded-xl border border-line">
                <div className="grid grid-cols-[1fr_auto] bg-white/5 px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-muted">
                  <span>Asset</span>
                  <span>Prompt reference</span>
                </div>
                {PROMPT_REFERENCE_TOKENS.map(([label, token]) => (
                  <div
                    key={token}
                    className="grid grid-cols-[1fr_auto] items-center border-t border-line px-3 py-2.5 text-[12px]"
                  >
                    <span>{label}</span>
                    <button
                      type="button"
                      title={`Insert ${token} into prompt`}
                      onClick={() =>
                        setPromptText((current) =>
                          `${current}${current.trim() ? " " : ""}${token}`,
                        )
                      }
                      className="rounded-md border border-white/10 bg-white/8 px-2 py-1 font-mono text-[11px] text-white/80 transition hover:border-[var(--accent)]/50 hover:bg-[var(--accent)]/15 hover:text-white"
                    >
                      {token}
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-5 text-muted">
                Click a token to insert it. Product uploads are sent in this exact order, so the reference remains stable.
              </p>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Prompt instructions
              </div>
              <textarea
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                className={`${inputClass} min-h-32`}
                placeholder="Example: Create a premium handheld product demo with natural lighting and smooth close-up camera motion."
              />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Reference asset policy
              </div>
              <textarea
                value={referencePolicy}
                onChange={(e) => setReferencePolicy(e.target.value)}
                className={`${inputClass} min-h-28`}
                placeholder="How Image 1, Image 2 and Video 1 must be followed"
              />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Negative prompt / constraints
              </div>
              <textarea
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                className={`${inputClass} min-h-24`}
                placeholder="No redesign, deformation, duplicate products, invented text, extra buttons, or altered logos."
              />
            </div>
            <p className="text-[11px] leading-5 text-muted">
              EasyShot maps uploaded assets to Image 1, Image 2, Video 1… when submitting the API request.
            </p>
          </section>
        )}

        {moduleKey === "aigc" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Video generation provider
              </div>
              <select
                className={inputClass}
                value={videoProvider}
                onChange={(e) => {
                  const provider = e.target.value;
                  setVideoProvider(provider);
                  setVideoEngine(VIDEO_ENGINES[provider]?.[0]?.[0] ?? "");
                }}
              >
                {VIDEO_PROVIDERS.map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Video generation engine
              </div>
              <select
                className={inputClass}
                value={videoEngine}
                onChange={(e) => setVideoEngine(e.target.value)}
              >
                {(VIDEO_ENGINES[videoProvider] ?? []).map(([id, label]) => (
                  <option key={id} value={id}>{label} · {id}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Generation mode
              </div>
              <select
                className={inputClass}
                value={generationMode}
                onChange={(e) => setGenerationMode(e.target.value)}
              >
                <option value="reference-to-video">Reference assets → video</option>
                <option value="image-to-video">Image → video</option>
                <option value="text-to-video">Text → video</option>
                <option value="avatar-video">Avatar → video</option>
                <option value="audio-driven-avatar">Audio + avatar → talking video</option>
              </select>
            </div>
            <p className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] leading-5 text-muted">
              Saving this module updates the template's active render provider and engine. API credentials remain server-side environment variables.
            </p>
          </section>
        )}

        {moduleKey === "infinitetalk" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Required API inputs
              </div>
              <div className="overflow-hidden rounded-xl border border-line">
                {[
                  ["image", "One person or pre-composited image URL", "Required"],
                  ["audio", "WAV/MP3/M4A URL produced by the selected TTS voice", "Required"],
                  ["prompt", "Generation instructions · 1–4,000 characters", "Required"],
                ].map(([field, description, rule]) => (
                  <div
                    key={field}
                    className="border-t border-line px-3 py-3 first:border-t-0"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <code className="text-[11px] text-white/90">{field}</code>
                      <Badge tone="accent">{rule}</Badge>
                    </div>
                    <div className="mt-1 text-[11px] leading-5 text-muted">
                      {description}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">
                Managed by EasyShot
              </div>
              <div className="rounded-xl border border-line bg-white/5 px-3 py-3 text-[11px] leading-5 text-muted">
                EasyShot generates the TTS WAV first, signs private R2 URLs for the audio
                and photo, then submits the three inputs to RunPod Hub. Job polling,
                cancellation, video validation, and permanent R2 storage are automatic.
              </div>
            </div>
            <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[11px] leading-5 text-emerald-200/90">
              Fish Audio generates the speech WAV for every preset or cloned voice before
              RunPod Hub InfiniteTalk starts. RunPod Hub accepts a photo, audio URL, and
              prompt; the audio length determines the video length. Self-hosted and fal stay
              available as rollback backends.
            </p>
          </section>
        )}

        {moduleKey === "llm" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">LLM provider</div>
              <select
                className={inputClass}
                value={llmProvider}
                onChange={(e) => {
                  const provider = e.target.value;
                  setLlmProvider(provider);
                  setLlmModel(LLM_MODELS[provider]?.[0]?.[0] ?? "");
                }}
              >
                <option value="modelark">BytePlus ModelArk</option>
                <option value="anthropic">Anthropic</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">LLM model</div>
              <select
                className={inputClass}
                value={llmModel}
                onChange={(e) => setLlmModel(e.target.value)}
              >
                {(LLM_MODELS[llmProvider] ?? []).map(([id, label]) => (
                  <option key={id} value={id}>{label} · {id}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Use this model for</div>
              <select
                className={inputClass}
                value={llmPurpose}
                onChange={(e) => setLlmPurpose(e.target.value)}
              >
                <option value="product-analysis-and-script">Product analysis + script</option>
                <option value="script">Script generation</option>
                <option value="prompt">Prompt generation</option>
              </select>
            </div>
            <p className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] leading-5 text-muted">
              The script pipeline reads this module when it is present on the canvas. Provider API keys stay in server environment variables.
            </p>
          </section>
        )}

        {moduleKey === "keywords" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Keywords per scene</div>
              <input className={inputClass} type="number" min={1} max={8} value={keywordCount} onChange={(e) => setKeywordCount(Number(e.target.value))} />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Search language</div>
              <select className={inputClass} value={keywordLanguage} onChange={(e) => setKeywordLanguage(e.target.value)}>
                <option value="en">English</option>
                <option value="zh">Chinese</option>
                <option value="auto">Follow script language</option>
              </select>
            </div>
            <label className={checkboxCls}>
              <input type="checkbox" checked={matchScriptOrder} onChange={(e) => setMatchScriptOrder(e.target.checked)} />
              Generate and match terms in storyboard order
            </label>
          </section>
        )}

        {moduleKey === "materials" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Material strategy</div>
              <select className={inputClass} value={materialMode} onChange={(e) => setMaterialMode(e.target.value)}>
                <option value="hybrid">Hybrid · real assets → stock → AI gaps</option>
                <option value="real_only">Real product and stock assets only</option>
                <option value="ai_first">AI-generated clips first</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Material providers</div>
              <div className="grid gap-2">
                {MATERIAL_PROVIDERS.map(([id, label]) => (
                  <label key={id} className={checkboxCls}>
                    <input type="checkbox" checked={materialProviders.includes(id)} onChange={() => toggle(materialProviders, setMaterialProviders, id)} />
                    {label}
                  </label>
                ))}
              </div>
              <p className="mt-2 text-[11px] leading-5 text-muted">Priority follows this list: preserve real product identity first, use licensed stock for supporting scenes, and call Seedance only when a storyboard scene still has no usable visual.</p>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">When no matching footage is found</div>
              <select className={inputClass} value={materialFallback} onChange={(e) => setMaterialFallback(e.target.value)}>
                <option value="product_image">Animate a product image</option>
                <option value="ai_video">Generate an AI video clip</option>
                <option value="fail">Fail the job</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Target clip duration (seconds)</div>
              <input className={inputClass} type="number" min={1} max={15} step={0.5} value={clipDurationSec} onChange={(e) => setClipDurationSec(Number(e.target.value))} />
            </div>
            <label className={checkboxCls}>
              <input type="checkbox" checked={stockLicenseRequired} onChange={(e) => setStockLicenseRequired(e.target.checked)} />
              Require a commercial-use license record
            </label>
          </section>
        )}

        {moduleKey === "tts" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">TTS provider</div>
              <select className={inputClass} value={ttsProvider} onChange={(e) => setTtsProvider(e.target.value)}>
                <option value="fish_audio">Fish Audio (preset + private cloned voices)</option>
                <option value="custom_audio">Customer audio</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Provider voice ID</div>
              <input className={inputClass} value={ttsVoiceId} onChange={(e) => setTtsVoiceId(e.target.value)} placeholder="Empty = selected EasyShot voice" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Speed</div>
                <input className={inputClass} type="number" min={0.5} max={2} step={0.05} value={ttsSpeed} onChange={(e) => setTtsSpeed(Number(e.target.value))} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Volume</div>
                <input className={inputClass} type="number" min={0} max={2} step={0.05} value={ttsVolume} onChange={(e) => setTtsVolume(Number(e.target.value))} />
              </div>
            </div>
          </section>
        )}

        {moduleKey === "subtitles" && (
          <section className="mt-6 space-y-5">
            <label className={checkboxCls}>
              <input type="checkbox" checked={subtitleEnabled} onChange={(e) => setSubtitleEnabled(e.target.checked)} />
              Burn captions into the final video
            </label>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Caption mode</div>
              <select className={inputClass} value={subtitleMode} onChange={(e) => setSubtitleMode(e.target.value)}>
                <option value="sentence">Sentence captions</option>
                <option value="word_highlight">Word-by-word highlight</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Font</div>
                <input className={inputClass} value={subtitleFont} onChange={(e) => setSubtitleFont(e.target.value)} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Size</div>
                <input className={inputClass} type="number" min={18} max={120} value={subtitleSize} onChange={(e) => setSubtitleSize(Number(e.target.value))} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Text colour</div>
                <input className={`${inputClass} h-11`} type="color" value={subtitleColor} onChange={(e) => setSubtitleColor(e.target.value.toUpperCase())} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Highlight colour</div>
                <input className={`${inputClass} h-11`} type="color" value={subtitleHighlightColor} onChange={(e) => setSubtitleHighlightColor(e.target.value.toUpperCase())} />
              </div>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Position</div>
              <select className={inputClass} value={subtitlePosition} onChange={(e) => setSubtitlePosition(e.target.value)}>
                <option value="top">Top</option>
                <option value="center">Centre</option>
                <option value="bottom">Bottom</option>
              </select>
            </div>
          </section>
        )}

        {moduleKey === "bgm" && (
          <section className="mt-6 space-y-5">
            <label className={checkboxCls}>
              <input type="checkbox" checked={bgmEnabled} onChange={(e) => setBgmEnabled(e.target.checked)} />
              Add background music
            </label>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Music source</div>
              <select className={inputClass} value={bgmSource} onChange={(e) => setBgmSource(e.target.value)}>
                <option value="library">Licensed EasyShot library</option>
                <option value="upload">Customer upload</option>
                <option value="generated">AI-generated music</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Track ID</div>
              <input className={inputClass} value={bgmTrackId} onChange={(e) => setBgmTrackId(e.target.value)} placeholder="Empty = automatic match" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Music dB</div>
                <input className={inputClass} type="number" min={-60} max={0} value={bgmVolumeDb} onChange={(e) => setBgmVolumeDb(Number(e.target.value))} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Voice ducking dB</div>
                <input className={inputClass} type="number" min={-24} max={0} value={voiceDuckingDb} onChange={(e) => setVoiceDuckingDb(Number(e.target.value))} />
              </div>
            </div>
          </section>
        )}

        {moduleKey === "compose" && (
          <section className="mt-6 space-y-5">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Width</div>
                <input className={inputClass} type="number" min={320} max={3840} step={2} value={width} onChange={(e) => setWidth(Number(e.target.value))} />
              </div>
              <div>
                <div className="mb-2 text-[12px] font-medium text-muted">Height</div>
                <input className={inputClass} type="number" min={320} max={3840} step={2} value={height} onChange={(e) => setHeight(Number(e.target.value))} />
              </div>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Frames per second</div>
              <select className={inputClass} value={fps} onChange={(e) => setFps(Number(e.target.value))}>
                <option value={24}>24 fps</option>
                <option value={25}>25 fps</option>
                <option value={30}>30 fps</option>
                <option value={60}>60 fps</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Transition</div>
              <select className={inputClass} value={transition} onChange={(e) => setTransition(e.target.value)}>
                <option value="none">Hard cut</option>
                <option value="fade">Fade</option>
                <option value="slide">Slide</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Transition duration (ms)</div>
              <input className={inputClass} type="number" min={0} max={2000} step={50} value={transitionDurationMs} onChange={(e) => setTransitionDurationMs(Number(e.target.value))} />
            </div>
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-200/90">Configuration only — the MoneyPrinterTurbo renderer is not wired to this node yet.</p>
          </section>
        )}

        {moduleKey === "image_compose" && (
          <section className="mt-6 space-y-5">
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Provider</div>
              <input className={inputClass} value={imageProvider} readOnly />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Image model</div>
              <input className={inputClass} value={imageEngine} readOnly />
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Aspect ratio</div>
              <select className={inputClass} value={imageAspectRatio} onChange={(e) => setImageAspectRatio(e.target.value)}>
                <option value="9:16">Portrait — 1024 × 1536</option>
                <option value="16:9">Landscape — 1536 × 1024</option>
                <option value="1:1">Square — 1024 × 1024</option>
                <option value="auto">Default portrait — 1024 × 1536</option>
              </select>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Composition instructions</div>
              <textarea className={inputClass} rows={8} value={promptText} onChange={(e) => setPromptText(e.target.value)} />
            </div>
            <p className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[11px] leading-5 text-emerald-100/90">
              GPT Image 1.5 Stable combines the person and background before InfiniteTalk. Low quality, high input fidelity; AI infers scale and perspective. No manual placement editor or guaranteed exact likeness. Image ratios are 2:3, 3:2 or 1:1, not exact video ratios.
            </p>
          </section>
        )}

        {moduleKey === "publish" && (
          <section className="mt-6 space-y-5">
            <label className={checkboxCls}>
              <input type="checkbox" checked={publishEnabled} onChange={(e) => setPublishEnabled(e.target.checked)} />
              Publish automatically after R2 output is ready
            </label>
            <div>
              <div className="mb-2 text-[12px] font-medium text-muted">Platforms</div>
              <div className="grid gap-2">
                {PUBLISH_PLATFORMS.map(([id, label]) => (
                  <label key={id} className={checkboxCls}>
                    <input type="checkbox" checked={publishPlatforms.includes(id)} onChange={() => toggle(publishPlatforms, setPublishPlatforms, id)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <p className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] leading-5 text-amber-200/90">Configuration only — user-level social account connections are not wired yet.</p>
          </section>
        )}

        {/* 渲染 provider */}
        {moduleKey === "render" && (
          <section className="mt-6 space-y-3">
            <div className="text-[12px] font-medium text-muted">
              External API binding
            </div>
            <select
              className={inputClass}
              value={providerType}
              onChange={(e) => setProviderType(e.target.value)}
            >
              {VIDEO_PROVIDERS.map(([id, label]) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
            <input
              className={inputClass}
              placeholder="Engine, e.g. avatar_iii"
              value={engine}
              onChange={(e) => setEngine(e.target.value)}
            />
            <input
              className={inputClass}
              placeholder="Custom endpoint (optional)"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
            />
          </section>
        )}

        {/* 头像池：预设人物多选 */}
        {moduleKey === "avatar" && (
          <section className="mt-6">
            <div className="mb-2 text-[12px] font-medium text-muted">
              Preset roster for this template
              <span className="ml-1 font-normal">
                (none selected = full roster)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {avatars.map((a) => (
                <label key={a.id} className={checkboxCls}>
                  <input
                    type="checkbox"
                    checked={pool.includes(a.id)}
                    onChange={() => toggle(pool, setPool, a.id)}
                  />
                  <AvatarPortrait
                    avatarId={a.id}
                    className="size-6 rounded-full"
                  />
                  <span className={a.enabled ? "" : "opacity-40"}>
                    {a.name}
                  </span>
                  {a.premium && <Badge tone="accent">P</Badge>}
                </label>
              ))}
            </div>
          </section>
        )}

        {/* 声音池 */}
        {moduleKey === "voice" && (
          <section className="mt-6">
            <div className="mb-2 text-[12px] font-medium text-muted">
              Preset voices for this template
              <span className="ml-1 font-normal">
                (none selected = full list)
              </span>
            </div>
            <div className="grid gap-2">
              {voices.map((v) => (
                <label key={v.id} className={checkboxCls}>
                  <input
                    type="checkbox"
                    checked={pool.includes(v.id)}
                    onChange={() => toggle(pool, setPool, v.id)}
                  />
                  <span className={v.enabled ? "" : "opacity-40"}>
                    {v.name} · {v.locale} · {v.speed}x
                  </span>
                </label>
              ))}
            </div>
          </section>
        )}

        {/* 抠像：键色 + 交付格式 */}
        {moduleKey === "chroma" && (
          <>
            <p className={`mt-5 rounded-lg border px-3 py-2 text-[12px] leading-5 ${
              template.provider.type === "infinitetalk"
                ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200/90"
                : "border-amber-500/30 bg-amber-500/10 text-amber-200/90"
            }`}>
              {template.provider.type === "infinitetalk"
                ? "已接入：Cloudflare 先输出透明人物，RunPod 在同一任务内完成键色、背景合成并上传最终 MP4。"
                : "This post-processing module is not wired for the selected renderer yet."}
            </p>
            <section className="mt-6">
              <div className="mb-2 text-[12px] font-medium text-muted">
                Key colour
              </div>
              <div className="grid gap-2">
                {KEY_COLORS.map(([hex, label]) => (
                  <label key={hex} className={checkboxCls}>
                    <input
                      type="radio"
                      name="chroma-key"
                      checked={keyColor === hex}
                      onChange={() => setKeyColor(hex)}
                    />
                    <span
                      className="size-3.5 shrink-0 rounded-sm"
                      style={{ background: hex }}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </section>
            <section className="mt-6">
              <div className="mb-2 text-[12px] font-medium text-muted">
                Deliverables
                <span className="ml-1 font-normal">
                  (what the customer downloads)
                </span>
              </div>
              <div className="grid gap-2">
                {CHROMA_DELIVERABLES.map(([k, label]) => (
                  <label key={k} className={checkboxCls}>
                    <input
                      type="checkbox"
                      checked={deliverables.includes(k)}
                      onChange={() => toggle(deliverables, setDeliverables, k)}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </section>
          </>
        )}

        {/* 输入形式 */}
        {INPUT_OPTIONS[moduleKey] && (
          <section className="mt-6">
            <div className="mb-2 text-[12px] font-medium text-muted">
              Input formats
            </div>
            <div className="grid gap-2">
              {INPUT_OPTIONS[moduleKey].map(([k, label]) => (
                <label key={k} className={checkboxCls}>
                  <input
                    type="checkbox"
                    checked={inputs.includes(k)}
                    onChange={() => toggle(inputs, setInputs, k)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </section>
        )}

        {/* 输出要素（来自容器格式定义） */}
        {schemas[moduleKey] && INPUT_OPTIONS[moduleKey] && (
          <section className="mt-6">
            <div className="mb-2 text-[12px] font-medium text-muted">
              Output elements
              <span className="ml-1 font-normal">
                (from the container's recording format)
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {schemas[moduleKey].map((el) => (
                <label key={el.key} className={checkboxCls}>
                  <input
                    type="checkbox"
                    checked={outputs.includes(el.key)}
                    onChange={() => toggle(outputs, setOutputs, el.key)}
                  />
                  <span className="truncate" title={el.label}>
                    {el.label}
                  </span>
                  {el.provider && <Badge tone="accent">{el.provider}</Badge>}
                </label>
              ))}
            </div>
          </section>
        )}

        <div className="mt-8 flex gap-2">
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? <Spinner /> : "Save"}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
}
